import {
  type ApolloClient,
  ApolloProvider,
  type NormalizedCacheObject,
} from '@apollo/client';
import {
  AppContextProvider,
  ConfigProvider,
  type Logger,
  LoggerProvider,
} from '@seek/libs-shared';
import { MelwaysProvider } from '@seek/melways-react';
import { VocabProvider } from '@vocab/react';
import { BraidProvider } from 'braid-design-system';
import theme from 'braid-design-system/themes/seekJobs';
import { Outlet } from 'react-router';

import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import { CustomLink } from './shared/components/CustomLink';
import type { AppConfig } from './shared/config';
import {
  type AuthContextProps,
  AuthProvider,
} from './shared/providers/authProvider';
import { FeatureFlagProvider } from './shared/providers/featureFlagProvider';
import { ServiceProvider } from './shared/providers/serviceProvider';
import type { Services } from './shared/services';

interface AppProps {
  client: ApolloClient<NormalizedCacheObject>;
  config: AppConfig;
  logger: Logger;
  services: Services;
}

export const App = ({ client, config, services, logger }: AppProps) => {
  let auth: AuthContextProps = undefined;
  if (config.environment === 'local') {
    auth = {
      authenticationStatus: AUTHENTICATION_TYPES.AUTHENTICATED,
      userProfile: {
        firstName: 'John',
        lastName: 'Doe',
        email: 'test@example.com',
      },
      userId: 123,
    };
  }
  return (
    <ConfigProvider config={config}>
      <MelwaysProvider.Server
        site={config.siteName}
        url={config.absoluteUrl}
        alternative={config.consolidated ? 'subDomainRegion' : undefined}
        staging={
          config.environment === 'staging' ||
          config.environment === 'development'
        }
      >
        <LoggerProvider logger={logger}>
          <ApolloProvider client={client}>
            <ServiceProvider services={services}>
              <AppContextProvider>
                <AuthProvider auth={auth}>
                  <FeatureFlagProvider>
                    <BraidProvider theme={theme} linkComponent={CustomLink}>
                      <VocabProvider
                        language={config.language}
                        locale={config.locale}
                      >
                        <Outlet context={{ client, config }} />
                      </VocabProvider>
                    </BraidProvider>
                  </FeatureFlagProvider>
                </AuthProvider>
              </AppContextProvider>
            </ServiceProvider>
          </ApolloProvider>
        </LoggerProvider>
      </MelwaysProvider.Server>
    </ConfigProvider>
  );
};
